import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDispositivos } from '../../hooks/useDispositivos';
import { DispositivoInterface } from '../../interfaces/DispositivoInterface';
import { LogOut, Smartphone, Users, AlertCircle, CheckCircle } from 'lucide-react';

const Dashboard: React.FC = () => {
  const { usuario, logout } = useAuth();
  const { dispositivos, isLoading } = useDispositivos();

  const getEstadoFromId = (estatus_id: number): string => {
    switch (estatus_id) {
      case 1: return 'disponible';
      case 2: return 'asignado';
      case 3: return 'mantenimiento';
      case 4: return 'dañado';
      default: return 'unknown';
    }
  };

  const getEstadisticas = () => {
    const total = dispositivos.length;
    const disponibles = dispositivos.filter(d => getEstadoFromId(d.estatus_id) === 'disponible').length;
    const asignados = dispositivos.filter(d => getEstadoFromId(d.estatus_id) === 'asignado').length;
    const mantenimiento = dispositivos.filter(d => getEstadoFromId(d.estatus_id) === 'mantenimiento').length;
    const dañados = dispositivos.filter(d => getEstadoFromId(d.estatus_id) === 'dañado').length;

    return { total, disponibles, asignados, mantenimiento, dañados };
  };

  const estadisticas = getEstadisticas();

  const getEstadoColor = (estatus_id: number) => {
    const estado = getEstadoFromId(estatus_id);
    switch (estado) {
      case 'disponible':
        return 'bg-green-100 text-green-800';
      case 'asignado':
        return 'bg-blue-100 text-blue-800';
      case 'mantenimiento':
        return 'bg-yellow-100 text-yellow-800';
      case 'dañado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoIcon = (estatus_id: number) => {
    const estado = getEstadoFromId(estatus_id);
    switch (estado) {
      case 'disponible':
        return <CheckCircle className="w-4 h-4" />;
      case 'asignado':
        return <Users className="w-4 h-4" />;
      case 'mantenimiento':
      case 'dañado':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Smartphone className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando dispositivos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Samsung Device Manager</h1>
              <p className="text-gray-600">Bienvenido, {usuario?.nombre} {usuario?.apellido_paterno}</p>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Estadísticas */}
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Smartphone className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                      <dd className="text-lg font-medium text-gray-900">{estadisticas.total}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Disponibles</dt>
                      <dd className="text-lg font-medium text-gray-900">{estadisticas.disponibles}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Asignados</dt>
                      <dd className="text-lg font-medium text-gray-900">{estadisticas.asignados}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Mantenimiento</dt>
                      <dd className="text-lg font-medium text-gray-900">{estadisticas.mantenimiento}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Dañados</dt>
                      <dd className="text-lg font-medium text-gray-900">{estadisticas.dañados}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Dispositivos */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Dispositivos</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Lista completa de dispositivos Samsung
              </p>
            </div>
            <ul className="divide-y divide-gray-200">
              {dispositivos.map((dispositivo: DispositivoInterface) => (
                <li key={dispositivo.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {getEstadoIcon(dispositivo.estatus_id)}
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {dispositivo.nombre_dispositivo}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(dispositivo.estatus_id)}`}>
                              {getEstadoFromId(dispositivo.estatus_id)}
                            </span>
                          </div>
                          <div className="mt-2 flex">
                            <div className="flex items-center text-sm text-gray-500">
                              <p>
                                Modelo: {dispositivo.modelo || 'N/A'} | Serie: {dispositivo.numero_serie}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {dispositivo.asignacionActual?.usuario && (
                          <p className="text-sm text-gray-500">
                            Asignado a: {dispositivo.asignacionActual.usuario.nombre}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
